import React, { useEffect, useRef, useState } from 'react';

import { createUseStyles } from 'react-jss';
import { Icon } from 'ctm-components';
import classNames from 'classnames';

import PropTypes from 'prop-types';
import { REDIRECT_DELAY_MS } from 'ctm-components/components/prefill/utils';

const useStyles = createUseStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        marginBottom: '2rem'
    },
    list: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        width: '100%',
        marginBottom: '1rem'
    },
    progressBar: {
        width: '100%',
        height: '0.6rem',
        appearance: 'none',
        // Webkit browsers
        '&::-webkit-progress-bar': {
            backgroundColor: 'var(--progress-bar-inactive)'
        },
        '&::-webkit-progress-value': {
            backgroundColor: 'var(--progress-bar-active)'
        },
        // Firefox
        '&::-moz-progress-bar': {
            backgroundColor: 'var(--progress-bar-active)'
        },
        // IE/Edge
        '&::-ms-fill': {
            backgroundColor: 'var(--progress-bar-active)'
        },
        marginBottom: '1rem'
    },
    percentText: {
        fontSize: '1.8rem',
        fontWeight: '600',
        lineHeight: '2.8rem'
    },
    bucket: {
        fontSize: '1.6rem',
        display: 'flex',
        width: '100%',
        justifyContent: 'space-evenly',
        '@media only screen and (max-width: 768px)': {
            flexDirection: 'column',
            justifyContent: 'flex-end'
        }
    },
    bucketActive: {
        color: 'var(--body-color)'
    },
    bucketInactive: {
        color: 'var(--brand-base-60)'
    }
}));

const roundToTen = (num) => {
    if (num < 10) {
        return '0';
    }

    if (num < 100) {
        return `${num.toString()[0]}0`;
    }

    return 100;
};

const Bucket = ({ label, completed }) => {
    const classes = useStyles();

    return (
        <div data-testid="bucket-container"
            className={classNames(
                classes.bucket,
                completed ? classes.bucketActive : classes.bucketInactive
            )}>
            {completed && (
                <span>
                    <Icon variant="ctm_skinny_tick" color="brand_secondary" />
                    {' '}
                </span>
            )}
            <span>
                {label}
            </span>
        </div>
    );
};

Bucket.propTypes = {
    label: PropTypes.string.isRequired,
    completed: PropTypes.bool.isRequired
};

export const PrefillProgressBar = ({ buckets, percentageComplete }) => {
    const classes = useStyles();
    const showBuckets = percentageComplete !== 100;

    const [displayedPercentage, setDisplayedPercentage] = useState(0);
    const animationRef = useRef(null);
    const startTimeRef = useRef(null);

    // Animation effect
    useEffect(() => {
        if (showBuckets) {
            setDisplayedPercentage(percentageComplete);
            return;
        }

        // Start with current displayed value
        const endValue = percentageComplete;
        const duration = REDIRECT_DELAY_MS;

        // Animation function
        const animateProgress = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;

            if (elapsed < duration) {
                // Calculate current progress with easing
                const progress = elapsed / duration;
                const currentValue = progress * 100;
                setDisplayedPercentage(Math.round(currentValue));
                animationRef.current = requestAnimationFrame(animateProgress);
            } else {
                // Animation complete
                setDisplayedPercentage(endValue);
                startTimeRef.current = null;
            }
        };

        // Start animation
        animationRef.current = requestAnimationFrame(animateProgress);

        // Cleanup
        // eslint-disable-next-line consistent-return
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [percentageComplete, showBuckets, displayedPercentage]);

    return (
        <div className={classes.container}>
            {showBuckets && (
                <div className={classes.list}>
                    {buckets.map((name, index) => (
                        <Bucket
                            key={name}
                            label={name}
                            completed={
                                percentageComplete > (index / buckets.length) * 100
                            }
                        />
                    ))}
                </div>
            )}
            <progress className={classes.progressBar} max={100} value={displayedPercentage}>
                {displayedPercentage}
            </progress>
            <p className={classes.percentText}>
                {roundToTen(displayedPercentage)}
                {'% '}
                complete
            </p>
        </div>
    );
};

PrefillProgressBar.propTypes = {
    buckets: PropTypes.arrayOf(PropTypes.string).isRequired,
    percentageComplete: PropTypes.number.isRequired
};
